// notificationAlerts — cross-cutting alert side effects for new notifications:
// OS/desktop notifications, a sound chime, and the user prefs that gate them.
// The in-app toast + bell badge live in NotificationBell; this module owns the
// surfaces that reach outside the tab. Both surfaces are opt-in (default off).

const PREF_DESKTOP = 'bp.notif.desktop';
const PREF_SOUND = 'bp.notif.sound';

export function getDesktopPref(): boolean {
  return localStorage.getItem(PREF_DESKTOP) === '1';
}
export function setDesktopPref(on: boolean): void {
  localStorage.setItem(PREF_DESKTOP, on ? '1' : '0');
}
export function getSoundPref(): boolean {
  return localStorage.getItem(PREF_SOUND) === '1';
}
export function setSoundPref(on: boolean): void {
  localStorage.setItem(PREF_SOUND, on ? '1' : '0');
}

/** True when the browser exposes the Notification API and the user has granted
 *  permission. */
export function osPermissionGranted(): boolean {
  return typeof Notification !== 'undefined' && Notification.permission === 'granted';
}

/** Ask for OS-notification permission. Safe to call repeatedly — the browser
 *  no-ops once a decision is made. Returns the resulting permission. */
export async function requestOsPermission(): Promise<NotificationPermission> {
  if (typeof Notification === 'undefined') return 'denied';
  if (Notification.permission !== 'default') return Notification.permission;
  try {
    return await Notification.requestPermission();
  } catch {
    return Notification.permission;
  }
}

interface OsNotifyArgs {
  title: string;
  body: string;
  tag?: string;
  onClick?: () => void;
}

/** Fire a native OS notification when the window is backgrounded or unfocused.
 *  A focused, visible tab already shows the in-app toast, so we skip it then to
 *  avoid double-alerting. Works in the browser and in the Tauri desktop shell
 *  (the notification plugin + capability are enabled), which surfaces the same
 *  web Notification API natively. */
export function osNotify({ title, body, tag, onClick }: OsNotifyArgs): void {
  if (!getDesktopPref()) return;
  // hidden tab OR a visible tab whose window isn't focused → safe to OS-notify.
  if (!document.hidden && document.hasFocus()) return;
  if (!osPermissionGranted()) return;
  try {
    const n = new Notification(title, { body, tag, icon: '/favicon.ico' });
    n.onclick = () => {
      try {
        window.focus();
      } catch {
        /* */
      }
      onClick?.();
      n.close();
    };
  } catch {
    /* Notification construction can throw on some platforms */
  }
}

// Lazily-created shared AudioContext. Synthesizing a short two-tone chime
// avoids shipping a binary asset and respects autoplay policy (the context
// stays suspended until a user gesture has unlocked audio on the page).
let audioCtx: AudioContext | null = null;
function ctx(): AudioContext | null {
  try {
    const Ctor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    if (!audioCtx) audioCtx = new Ctor();
    return audioCtx;
  } catch {
    return null;
  }
}

/** Play a short, soft two-note chime. No-op when disabled, unavailable, or the
 *  audio context is still locked by the browser's autoplay policy. */
export function playChime(): void {
  if (!getSoundPref()) return;
  const ac = ctx();
  if (!ac) return;
  if (ac.state === 'suspended') {
    ac.resume().catch(() => {
      /* still locked */
    });
  }
  if (ac.state !== 'running') return;
  const t0 = ac.currentTime;
  for (const [freq, at] of [
    [880, 0],
    [1175, 0.12],
  ] as const) {
    const osc = ac.createOscillator();
    const gain = ac.createGain();
    osc.type = 'sine';
    osc.frequency.value = freq;
    const start = t0 + at;
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(0.15, start + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.18);
    osc.connect(gain).connect(ac.destination);
    osc.start(start);
    osc.stop(start + 0.2);
  }
}
